/**
 * Photo save's way out of the app (game-on-freeform U7), reachable without Capacitor.
 *
 * `useSaveImage` runs inside the share page's Astro island too, which is Capacitor-free by
 * design: `platform/index.ts` imports `@capacitor/core`, which the island cannot resolve, and
 * importing it there failed every web-deploy from U7 on (2026-10-27). So the hook calls
 * `shareImage` here. It is the web adapter's (the share sheet or a download) unless the app
 * registered the platform's at boot (`main.tsx`), which in the native shell is the cached-file
 * share. The same split as `soundSession.ts` for the SoundHost.
 *
 * No `@capacitor/*` here, and none in anything this imports.
 */
import type { ShareImage, ShareImageOutcome } from "./types";
import { WebPlatformAdapter } from "./WebPlatformAdapter";

export type ShareImageFn = (image: ShareImage, prefer: "share" | "download") => Promise<ShareImageOutcome>;

let registered: ShareImageFn | null = null;
let savesDirectly = false;

/** The app's boot hands over the platform's share; the island never calls this. */
export function setImageShare(fn: ShareImageFn | null, opts?: { savesDirectly?: boolean }): void {
  registered = fn;
  savesDirectly = !!fn && !!opts?.savesDirectly;
}

/** The registered share saves straight to Photos (no share sheet), so the picture does not
 *  leave the app and needs no grown-up check (U18). False on the web and when nothing is
 *  registered; an older native shell without the plugin keeps the sheet, and the check. */
export function imageSavesDirectly(): boolean {
  return savesDirectly;
}

export function shareImage(image: ShareImage, prefer: "share" | "download"): Promise<ShareImageOutcome> {
  if (registered) return registered(image, prefer);
  return new WebPlatformAdapter().shareImage(image, prefer);
}
